export const PLAYBACK_TIMEOUT_MS = 18000;

export const isPlaybackActive = (status) =>
  status === 'loading' || status === 'playing' || status === 'rebuffering';

// The sole owner of transport operations on the shared HTMLAudioElement.
export const createPlaybackController = (audio, { stream = 'myata' } = {}) => {
  let state = { stream, status: 'idle', error: null };
  let generation = 0;
  let attempt = null;
  let disposed = false;
  const subscribers = new Set();

  const publish = (next) => {
    state = next;
    subscribers.forEach(listener => listener(state));
  };

  const isCurrent = (candidate) =>
    !disposed && attempt === candidate && candidate.generation === generation;

  const clearDeadline = (candidate) => {
    clearTimeout(candidate.timer);
    candidate.timer = null;
  };

  const release = () => {
    // Invalidate callbacks before pause/load can queue more media events.
    generation += 1;
    const previous = attempt;
    attempt = null;
    if (previous) {
      clearDeadline(previous);
      Object.entries(previous.listeners).forEach(([event, listener]) => {
        audio.removeEventListener(event, listener);
      });
    }

    const hadSource = audio.hasAttribute('src') || Boolean(audio.currentSrc);
    audio.pause();
    // src='' can request the document URL. Remove the attribute instead.
    audio.removeAttribute('src');
    if (hadSource) audio.load();
  };

  const fail = (candidate, reason) => {
    if (!isCurrent(candidate)) return;
    release();
    publish({ ...state, status: 'error', error: reason });
  };

  const armDeadline = (candidate, reason) => {
    candidate.timer = setTimeout(() => fail(candidate, reason), PLAYBACK_TIMEOUT_MS);
  };

  const stop = () => {
    if (disposed) return;
    release();
    publish({ ...state, status: 'idle', error: null });
  };

  const start = (nextStream = state.stream) => {
    if (disposed || (state.stream === nextStream && isPlaybackActive(state.status))) return;
    release();
    const current = {
      generation,
      url: 'https://radio.dline-media.com/' + nextStream,
      timer: null,
      listeners: {},
    };
    attempt = current;

    const hasCurrentSource = () => audio.currentSrc === current.url;
    const handlePlaying = () => {
      // Also reject queued native events while a new source is still loading.
      if (!isCurrent(current) || !hasCurrentSource() || audio.paused ||
          audio.readyState < 3 || audio.ended || audio.error) return;
      clearDeadline(current);
      publish({ ...state, status: 'playing', error: null });
    };
    const handleBuffering = () => {
      if (!isCurrent(current) || state.status !== 'playing' || !hasCurrentSource() ||
          audio.paused || audio.readyState >= 3) return;
      // A stalled download with enough buffered audio is not interrupted playback.
      publish({ ...state, status: 'rebuffering', error: null });
      if (isCurrent(current)) armDeadline(current, 'rebuffer-timeout');
    };
    current.listeners = {
      playing: handlePlaying,
      waiting: handleBuffering,
      stalled: handleBuffering,
      error: () => {
        if (audio.error) fail(current, 'media-error');
      },
      ended: () => {
        if (audio.ended && hasCurrentSource()) fail(current, 'ended');
      },
      pause: () => {
        if (!isCurrent(current) || !audio.paused || !hasCurrentSource()) return;
        // Some browsers queue pause before ended/error for an interrupted stream.
        if (audio.error) fail(current, 'media-error');
        else if (audio.ended) fail(current, 'ended');
        else stop();
      },
    };
    Object.entries(current.listeners).forEach(([event, listener]) => {
      audio.addEventListener(event, listener);
    });
    publish({ stream: nextStream, status: 'loading', error: null });
    if (!isCurrent(current)) return;
    armDeadline(current, 'startup-timeout');

    try {
      audio.src = current.url;
      audio.load();
      // Only a genuine playing event completes startup, never a late promise.
      Promise.resolve(audio.play()).catch(() => fail(current, 'play-rejected'));
    } catch (_) {
      fail(current, 'play-rejected');
    }
  };

  return {
    getState: () => state,
    subscribe: (listener) => {
      if (disposed) return () => {};
      subscribers.add(listener);
      listener(state);
      return () => subscribers.delete(listener);
    },
    start,
    stop,
    toggle: () => isPlaybackActive(state.status) ? stop() : start(),
    selectStream: (nextStream) => {
      if (disposed || nextStream === state.stream) return;
      if (isPlaybackActive(state.status)) {
        start(nextStream);
      } else {
        release();
        publish({ stream: nextStream, status: 'idle', error: null });
      }
    },
    retry: () => {
      if (state.status === 'error') start();
    },
    dispose: () => {
      subscribers.clear();
      stop();
      disposed = true;
    },
  };
};
